import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type LeadStatus,
  getLead,
  getTool,
  listAeoPages,
  listEvents,
  listLeads,
  listTools,
  revenueDailySeries,
  sumRevenueByTool,
  updateLeadStatus,
} from '@tool-factory/registry';
import { supabase } from './supabase';

export function useTools() {
  return useQuery({ queryKey: ['tools'], queryFn: () => listTools(supabase) });
}
export function useTool(slug: string) {
  return useQuery({
    queryKey: ['tool', slug],
    queryFn: () => getTool(supabase, slug),
    enabled: !!slug,
  });
}
export function useLeads(toolId: string | undefined) {
  return useQuery({
    queryKey: ['leads', toolId],
    queryFn: () => listLeads(supabase, toolId === undefined ? {} : { toolId }),
    enabled: !!toolId,
  });
}
export function useEvents(toolId: string | undefined) {
  return useQuery({
    queryKey: ['events', toolId],
    // biome-ignore lint/style/noNonNullAssertion: query disabled when toolId is undefined
    queryFn: () => listEvents(supabase, toolId!, { limit: 200 }),
    enabled: !!toolId,
  });
}
export function useAeoPages(toolId: string | undefined) {
  return useQuery({
    queryKey: ['aeo', toolId],
    // biome-ignore lint/style/noNonNullAssertion: query disabled when toolId is undefined
    queryFn: () => listAeoPages(supabase, toolId!),
    enabled: !!toolId,
  });
}
export function useRevenueByTool(toolId: string | undefined) {
  return useQuery({
    queryKey: ['revenue', toolId],
    // biome-ignore lint/style/noNonNullAssertion: query disabled when toolId is undefined
    queryFn: () => sumRevenueByTool(supabase, toolId!),
    enabled: !!toolId,
    staleTime: 60_000,
  });
}
export function useRevenueByTools(toolIds: string[]) {
  return useQueries({
    queries: toolIds.map((id) => ({
      queryKey: ['revenue', id],
      queryFn: () => sumRevenueByTool(supabase, id),
      staleTime: 60_000,
    })),
  });
}
export function useRevenueDailySeries(
  toolId: string | null,
  range: { since: string; until: string },
) {
  return useQuery({
    queryKey: ['revenue-series', toolId, range.since, range.until],
    queryFn: () => revenueDailySeries(supabase, toolId, range),
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    // biome-ignore lint/style/noNonNullAssertion: query disabled when id is undefined
    queryFn: () => getLead(supabase, id!),
    enabled: !!id,
  });
}

export function useUpdateLeadStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { status: LeadStatus; patch?: Record<string, unknown> }) =>
      updateLeadStatus(supabase, id, vars.status, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead', id] }),
  });
}
